import React, { useEffect, useRef } from 'react';

import css from './OnLocationPage.module.css';

// Germany, shown while there is nothing to fit the map to.
const DEFAULT_CENTER = [10.45, 51.16];
const DEFAULT_ZOOM = 5;

/**
 * Mapbox map with one marker per spot. The library is the global `window.mapboxgl` that the app
 * already loads for the location search. `buildPopup(spot)` returns a DOM node (not HTML text), so
 * the photographer's note can never inject markup. `onSelect(id)` is called when a popup opens;
 * `selectedId` opens the matching popup (from the list). `origin` ({ lat, lng }) is the place the
 * visitor searched for or their own position: it gets its own blue marker, and the map shows it
 * together with the nearest spots (the list is then sorted nearest first).
 */
const SpotsMap = ({ spots, selectedId, onSelect, buildPopup, origin }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());

  useEffect(() => {
    const mapboxgl = typeof window !== 'undefined' ? window.mapboxgl : null;
    if (!mapboxgl || !containerRef.current) {
      return undefined;
    }
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v10',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    mapRef.current = map;
    const markers = markersRef.current;
    return () => {
      markers.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = window.mapboxgl;
    if (!map || !mapboxgl) {
      return;
    }
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current.clear();

    const bounds = new mapboxgl.LngLatBounds();
    (spots || []).forEach(spot => {
      if (!Number.isFinite(spot.lat) || !Number.isFinite(spot.lng)) {
        return;
      }
      const popup = new mapboxgl.Popup({ offset: 28 }).setDOMContent(buildPopup(spot));
      popup.on('open', () => onSelect(spot.id));
      const marker = new mapboxgl.Marker().setLngLat([spot.lng, spot.lat]).setPopup(popup).addTo(map);
      markersRef.current.set(spot.id, marker);
      bounds.extend([spot.lng, spot.lat]);
    });

    const hasOrigin = origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng);
    if (hasOrigin) {
      const originMarker = new mapboxgl.Marker({ color: '#2563eb' }).setLngLat([origin.lng, origin.lat]).addTo(map);
      markersRef.current.set('__origin__', originMarker);
      const fit = new mapboxgl.LngLatBounds([origin.lng, origin.lat], [origin.lng, origin.lat]);
      // The spots arrive nearest first: show the origin and the three nearest ones.
      (spots || [])
        .filter(s => Number.isFinite(s.lat) && Number.isFinite(s.lng))
        .slice(0, 3)
        .forEach(s => fit.extend([s.lng, s.lat]));
      if (markersRef.current.size > 1 && (spots || []).length > 0) {
        map.fitBounds(fit, { padding: 60, maxZoom: 13 });
      } else {
        map.easeTo({ center: [origin.lng, origin.lat], zoom: 11 });
      }
    } else if (markersRef.current.size === 1) {
      const [only] = spots.filter(s => markersRef.current.has(s.id));
      map.easeTo({ center: [only.lng, only.lat], zoom: 13 });
    } else if (markersRef.current.size > 1) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
    } else {
      map.easeTo({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    }
  }, [spots, buildPopup, onSelect, origin]);

  useEffect(() => {
    const map = mapRef.current;
    const marker = selectedId ? markersRef.current.get(selectedId) : null;
    if (!map || !marker) {
      return;
    }
    const popup = marker.getPopup();
    if (popup && !popup.isOpen()) {
      marker.togglePopup();
    }
    map.easeTo({ center: marker.getLngLat() });
  }, [selectedId]);

  return <div ref={containerRef} className={css.map} />;
};

export default SpotsMap;
